import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";

const services = ["Lavage des murs et plafonds", "Après déménagement", "Après construction", "Lavage des vitres et tapis", "Logements locatifs", "Bureaux et locaux commerciaux", "Restaurants et hôtels", "Entretien et grand ménage", "Intérieur de voiture"];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Veuillez remplir au moins le nom, l'email et le message.");
      return;
    }
    setSubmitting(true);

    const subject = encodeURIComponent(
      `Demande de devis — ${form.service} — ${form.name}`
    );
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite obtenir un devis pour : ${form.service}.\n\n` +
        `Nom : ${form.name}\n` +
        `Email : ${form.email}\n` +
        `Téléphone : ${form.phone || "(non fourni)"}\n` +
        `Adresse / Zone : ${form.address || "(non fournie)"}\n\n` +
        `Détails du besoin :\n${form.message}\n\n` +
        `Envoyé depuis Entretien Net Pro`
    );

    const bySms = e.nativeEvent.submitter?.value === "sms";
    window.location.href = bySms
      ? `sms:+14505781383?body=${body}`
      : `mailto:Entretiennetpro@gmail.com?subject=${subject}&body=${body}`;

    setTimeout(() => {
      toast.success(
        "Vérifiez votre demande dans votre application, puis envoyez-la."
      );
      setSubmitting(false);
    }, 600);
  };

  return (
    <section
      id="contact"
      className="relative bg-[#101D2C] text-white py-24 md:py-32 overflow-hidden grain"
      data-testid="contact-section"
    >
      <div className="absolute left-0 top-0 w-72 h-72 bg-[#58BCFA]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute right-10 bottom-20 w-40 h-40 stripes opacity-10" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="divider-tag on-dark">Contact</span>
          <h2 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            Parlons de
            <br />
            <span className="text-[#58BCFA]">votre projet.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-md">
            Par téléphone, texto ou courriel — parlons de vos besoins.
            Décrivez votre besoin pour demander une soumission.
          </p>

          <div className="mt-10 space-y-6">
            <a
              href="tel:+14505781383"
              data-testid="contact-phone-1"
              className="group flex items-start gap-4 hover:translate-x-1 transition-transform"
            >
              <div className="h-11 w-11 border border-[#58BCFA] flex items-center justify-center flex-shrink-0 group-hover:bg-[#58BCFA] transition-colors">
                <Phone className="h-5 w-5 text-[#58BCFA] group-hover:text-[#101D2C]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-white/50">
                  Téléphone
                </div>
                <div className="font-display font-bold text-xl text-white mt-1">
                  450 578 1383
                </div>
              </div>
            </a>



            <a href="mailto:Entretiennetpro@gmail.com" className="flex items-center gap-4" data-testid="contact-email">
              <Mail className="h-5 w-5 text-[#58BCFA] shrink-0" />
              <div className="min-w-0"><div className="text-sm text-white/60">Courriel</div><div className="font-display font-bold text-lg break-all">Entretiennetpro@gmail.com</div></div>
            </a>

            <a href="tel:+12633552601" className="flex items-center gap-4"><Phone className="h-5 w-5 text-[#58BCFA]"/><div><div className="text-sm text-white/60">Autre numéro</div><div className="font-display font-bold text-xl">263 355 2601</div></div></a>


          </div>
        </motion.div>

        {/* Right: form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="lg:col-span-7 bg-white text-[#101D2C] p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#101D2C]/60 mb-2">
            Demande de soumission
          </div>
          <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-8">
            Décrivez-nous votre besoin.
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="field-name" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Nom complet *
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                id="field-name" data-testid="form-name"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition"
              />
            </div>
            <div>
              <label htmlFor="field-email" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                id="field-email" data-testid="form-email"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition"
              />
            </div>
            <div>
              <label htmlFor="field-phone" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Téléphone
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                id="field-phone" data-testid="form-phone"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition"
              />
            </div>
            <div>
              <label htmlFor="field-service" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Service *
              </label>
              <select
                value={form.service}
                onChange={update("service")}
                id="field-service" data-testid="form-service"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition"
              >
                {services.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="field-address" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Adresse / Zone
              </label>
              <input
                type="text"
                value={form.address}
                onChange={update("address")}
                placeholder="Quartier, ville"
                id="field-address" data-testid="form-address"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="field-message" className="block text-xs uppercase tracking-widest font-semibold mb-2">
                Message *
              </label>
              <textarea
                rows={5}
                required
                value={form.message}
                onChange={update("message")}
                placeholder="Décrivez la surface, la fréquence souhaitée, vos besoins spécifiques…"
                id="field-message" data-testid="form-message"
                className="w-full border border-slate-300 px-4 py-3 bg-white text-[#101D2C] focus:border-[#101D2C] focus:ring-1 focus:ring-[#101D2C] outline-none transition resize-none"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="group inline-flex items-center justify-center gap-3 bg-[#101D2C] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#58BCFA] hover:text-[#101D2C] transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? "Ouverture…" : "Préparer mon courriel"}
              <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button type="submit" value="sms" disabled={submitting} className="px-6 py-4 border border-[#101D2C] font-semibold hover:bg-slate-100">Par texto</button>
          </div>

          <p className="mt-4 text-xs text-[#475569]">
            Votre demande sera préparée dans votre application de courriel à Entretiennetpro@gmail.com, ou par texto au 450 578 1383. Vérifiez-la puis envoyez-la dans votre application.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
